/**
 * Fitment Search Grid — storefront 用 collection grid replacement (Phase C, 2026-05-18).
 *
 * - URL state: vehicle / filters / sort / page を query string で管理
 * - /api/fitment/search-with-filters で paginated 結果取得
 * - /api/fitment/search-facets で sidebar 描画
 * - Empire の productgrid--items を hide、自前 card で render
 *
 * debug log: window.ARK_DEBUG !== false で [ark/search-grid] prefix
 */
(function () {
  'use strict';

  if (window.__arkSearchGridLoaded) return;
  window.__arkSearchGridLoaded = true;

  var API_BASE = (typeof window !== 'undefined' && window.ARK_API_BASE) || '/apps/fitment';
  var SEARCH_URL = API_BASE.replace(/\/api\/fitment$/, '/api/fitment/search-with-filters').replace(
    /\/apps\/fitment$/,
    '/api/fitment/search-with-filters'
  );
  var FACETS_URL = API_BASE.replace(/\/api\/fitment$/, '/api/fitment/search-facets').replace(
    /\/apps\/fitment$/,
    '/api/fitment/search-facets'
  );
  // 上の URL 組立は L3 direct CORS 想定 (https://app.sabaku-parts.com/api/fitment/search-with-filters)
  // App Proxy fallback の場合は /apps/fitment では sub-path 1 階層しか対応してないので、
  // L3 Direct CORS を期待する block 構成
  var SHOP_DOMAIN = typeof window !== 'undefined' ? window.ARK_SHOP_DOMAIN || null : null;

  var STORAGE_KEY = 'ark.selectedVehicle';
  var VEHICLE_CHANGED_EVENT = 'ark:vehicle-changed';
  var VEHICLE_CHANGE_REQUEST_EVENT = 'ark:vehicle-change-request';

  var DBG = function () {
    if (window.ARK_DEBUG !== false) {
      console.log.apply(
        console,
        ['[ark/search-grid]'].concat(Array.prototype.slice.call(arguments))
      );
    }
  };

  var DEBOUNCE_MS = 300;

  // ---------------------------------------------------------------------
  // Vehicle / state helpers
  // ---------------------------------------------------------------------

  function loadVehicle() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function vehicleLabel(v) {
    if (!v) return '';
    var s = v.year + ' ' + v.make + ' ' + v.model;
    if (v.submodel) s += ' ' + v.submodel;
    return s;
  }

  // ---------------------------------------------------------------------
  // URL state (query string ⇄ filter state)
  // ---------------------------------------------------------------------

  function readUrlState() {
    var params = new URLSearchParams(window.location.search);
    return {
      brand: (params.get('brand') || '')
        .split(',')
        .map(function (s) {
          return s.trim();
        })
        .filter(Boolean),
      priceMin: params.get('priceMin') ? parseFloat(params.get('priceMin')) : null,
      priceMax: params.get('priceMax') ? parseFloat(params.get('priceMax')) : null,
      // available: default = true (in stock only)
      // URL に "available=0" が明示されてる時のみ false
      available: params.get('available') !== '0',
      // includeUniversal: default = false (vehicle 適合のみ、universal 除外)
      includeUniversal: params.get('includeUniversal') === '1',
      category: params.get('category') || null,
      subcategory: params.get('subcategory') || null,
      sort: params.get('sort') || 'relevance',
      page: parseInt(params.get('page') || '1', 10) || 1,
    };
  }

  function writeUrlState(state, push) {
    var params = new URLSearchParams(window.location.search);
    // 既存 v / fitment_* は残す (vehicle 用)
    if (state.brand && state.brand.length > 0) params.set('brand', state.brand.join(','));
    else params.delete('brand');
    if (state.priceMin != null) params.set('priceMin', String(state.priceMin));
    else params.delete('priceMin');
    if (state.priceMax != null) params.set('priceMax', String(state.priceMax));
    else params.delete('priceMax');
    // available: default=true は URL から省略、false (=全表示) は明示
    if (state.available === false) params.set('available', '0');
    else params.delete('available');
    // includeUniversal: default=false は URL から省略、true (=含む) は明示
    if (state.includeUniversal === true) params.set('includeUniversal', '1');
    else params.delete('includeUniversal');
    if (state.category) params.set('category', state.category);
    else params.delete('category');
    if (state.subcategory) params.set('subcategory', state.subcategory);
    else params.delete('subcategory');
    if (state.sort && state.sort !== 'relevance') params.set('sort', state.sort);
    else params.delete('sort');
    if (state.page && state.page > 1) params.set('page', String(state.page));
    else params.delete('page');
    var qs = params.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    if (push) window.history.pushState(null, '', newUrl);
    else window.history.replaceState(null, '', newUrl);
  }

  // ---------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------

  function buildApiQs(vehicle, state, container) {
    var qs = new URLSearchParams();
    if (SHOP_DOMAIN) qs.set('shop', SHOP_DOMAIN);
    // Shopify 検索ページの キーワード (?q=)。collection/find-parts には無いので無影響。
    var kw = new URLSearchParams(window.location.search).get('q');
    if (kw && kw.trim()) qs.set('q', kw.trim());
    if (vehicle) {
      qs.set('year', String(vehicle.year));
      qs.set('make', vehicle.make);
      qs.set('model', vehicle.model);
      if (vehicle.submodel) qs.set('submodel', vehicle.submodel);
    }
    // コレクションのスコープ。PIM 側は ProductCollection の所属で絞るので、
    // ページに載っている商品と grid が返す商品が定義上一致する。
    // vehicle (YMM) とは独立で、両方あれば AND になる
    // (「ラングラーのページ」を開いた上で「2021 年式」を選ぶ)。
    if (container.__scopeCollection) qs.set('collection', container.__scopeCollection);
    if (state.brand.length > 0) qs.set('brand', state.brand.join(','));
    if (state.priceMin != null) qs.set('priceMin', String(state.priceMin));
    if (state.priceMax != null) qs.set('priceMax', String(state.priceMax));
    if (state.available === true) qs.set('available', '1');
    // includeUniversal は default が false なので明示送信 (backend default は true)
    qs.set('includeUniversal', state.includeUniversal ? '1' : '0');
    if (state.category) qs.set('category', state.category);
    if (state.subcategory) qs.set('subcategory', state.subcategory);
    qs.set('sort', state.sort);
    qs.set('page', String(state.page));
    qs.set('limit', container.dataset.pageSize || '24');
    return qs.toString();
  }

  function fetchSearch(vehicle, state, container) {
    return fetch(SEARCH_URL + '?' + buildApiQs(vehicle, state, container)).then(function (res) {
      if (!res.ok) throw new Error('search HTTP ' + res.status);
      return res.json();
    });
  }

  function fetchFacets(vehicle, state, container) {
    // brand filter 外して facets を取る (sidebar で switchable に)
    var stateNoBrand = Object.assign({}, state, { brand: [] });
    return fetch(FACETS_URL + '?' + buildApiQs(vehicle, stateNoBrand, container)).then(
      function (res) {
        if (!res.ok) throw new Error('facets HTTP ' + res.status);
        return res.json();
      }
    );
  }

  // ---------------------------------------------------------------------
  // Card render
  // ---------------------------------------------------------------------

  // API は USD を返す。Shopify マルチカレンシー rate (window.Shopify.currency) が
  // 取れれば表示通貨 (JPY 等) に換算、無ければ USD のまま (custom-fitment.js と同ロジック)。
  function formatPrice(amount, currencyCode) {
    if (amount == null) return '';
    var n = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(n)) return '';
    try {
      var cur = window.Shopify && window.Shopify.currency;
      if (cur && cur.active && cur.active !== (currencyCode || 'USD') && cur.rate) {
        var rate = parseFloat(cur.rate);
        if (isFinite(rate) && rate > 0) {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: cur.active,
          }).format(n * rate);
        }
      }
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
      }).format(n);
    } catch (_) {
      return '$' + n.toFixed(2);
    }
  }

  function renderCard(item) {
    var card = document.createElement('a');
    card.className = 'ark-search-grid__card';
    card.href = '/products/' + item.handle;
    card.setAttribute('data-fitment-search-card', '');
    card.setAttribute('data-product-gid', item.productGid);

    var imgWrap = document.createElement('div');
    imgWrap.className = 'ark-search-grid__card-img';
    if (item.imageUrl) {
      var img = document.createElement('img');
      img.src = item.imageUrl;
      img.alt = item.imageAlt || item.title;
      img.loading = 'lazy';
      imgWrap.appendChild(img);
    }
    card.appendChild(imgWrap);

    if (item.vendor) {
      var vendor = document.createElement('div');
      vendor.className = 'ark-search-grid__card-vendor';
      vendor.textContent = item.vendor;
      card.appendChild(vendor);
    }

    var title = document.createElement('div');
    title.className = 'ark-search-grid__card-title';
    title.textContent = item.title;
    card.appendChild(title);

    var priceWrap = document.createElement('div');
    priceWrap.className = 'ark-search-grid__card-price';
    var priceMin = item.priceMin;
    var priceMax = item.priceMax;
    var compareMin = item.compareAtPriceMin;
    var priceStr =
      priceMin === priceMax || !priceMax
        ? formatPrice(priceMin, item.currencyCode)
        : formatPrice(priceMin, item.currencyCode) +
          ' – ' +
          formatPrice(priceMax, item.currencyCode);
    var priceEl = document.createElement('span');
    priceEl.className = 'ark-search-grid__card-price-current';
    priceEl.textContent = priceStr;
    priceWrap.appendChild(priceEl);
    if (compareMin && parseFloat(compareMin) > parseFloat(priceMin || '0')) {
      var compareEl = document.createElement('span');
      compareEl.className = 'ark-search-grid__card-price-compare';
      compareEl.textContent = formatPrice(compareMin, item.currencyCode);
      priceWrap.appendChild(compareEl);
    }
    card.appendChild(priceWrap);

    if (!item.available) {
      var soldOut = document.createElement('span');
      soldOut.className = 'ark-search-grid__card-badge ark-search-grid__card-badge--sold-out';
      soldOut.textContent = '取り寄せ';
      card.appendChild(soldOut);
    }

    if (item.isUniversal) {
      var uni = document.createElement('span');
      uni.className = 'ark-search-grid__card-badge ark-search-grid__card-badge--universal';
      uni.textContent = '汎用';
      card.appendChild(uni);
    }

    return card;
  }

  // ---------------------------------------------------------------------
  // Pagination render
  // ---------------------------------------------------------------------

  function renderPagination(container, total, page, limit) {
    var nav = container.querySelector('[data-search-grid-pagination]');
    if (!nav) return;
    nav.innerHTML = '';
    var totalPages = Math.max(1, Math.ceil(total / limit));
    if (totalPages <= 1) return;

    function btn(label, targetPage, active, disabled) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ark-search-grid__page-btn';
      if (active) b.classList.add('is-active');
      if (disabled) b.disabled = true;
      b.textContent = label;
      b.addEventListener('click', function () {
        navigateToPage(container, targetPage);
      });
      return b;
    }

    nav.appendChild(btn('‹', page - 1, false, page === 1));

    // 動的 page 表示 (current ± 2)
    var pages = [];
    pages.push(1);
    for (var p = page - 2; p <= page + 2; p++) {
      if (p > 1 && p < totalPages) pages.push(p);
    }
    if (totalPages > 1) pages.push(totalPages);
    pages = pages.sort(function (a, b) {
      return a - b;
    });
    var dedup = [];
    var prev = 0;
    pages.forEach(function (p) {
      if (p === prev) return;
      if (p > prev + 1) {
        var ellipsis = document.createElement('span');
        ellipsis.className = 'ark-search-grid__page-ellipsis';
        ellipsis.textContent = '…';
        nav.appendChild(ellipsis);
      }
      nav.appendChild(btn(String(p), p, p === page, false));
      dedup.push(p);
      prev = p;
    });

    nav.appendChild(btn('›', page + 1, false, page === totalPages));
  }

  function navigateToPage(container, page) {
    var state = container.__arkState;
    state.page = page;
    writeUrlState(state, true);
    refresh(container, { rerenderFacets: false });
    window.scrollTo({ top: container.offsetTop - 100, behavior: 'smooth' });
  }

  // ---------------------------------------------------------------------
  // Sidebar (facets) render
  // ---------------------------------------------------------------------

  function renderSidebar(container, facets, state) {
    // Vehicle pill
    var vehiclePill = container.querySelector('[data-search-grid-vehicle]');
    if (vehiclePill) {
      vehiclePill.innerHTML = '';
      var v = loadVehicle();
      if (v) {
        var label = document.createElement('span');
        label.className = 'ark-search-grid__vehicle-label';
        label.textContent = vehicleLabel(v);
        vehiclePill.appendChild(label);

        var changeBtn = document.createElement('button');
        changeBtn.type = 'button';
        changeBtn.className = 'ark-search-grid__vehicle-change';
        changeBtn.textContent = '変更';
        changeBtn.addEventListener('click', function () {
          window.dispatchEvent(new CustomEvent(VEHICLE_CHANGE_REQUEST_EVENT));
        });
        vehiclePill.appendChild(changeBtn);
      } else {
        var noVehicle = document.createElement('button');
        noVehicle.type = 'button';
        noVehicle.className = 'ark-search-grid__vehicle-change';
        noVehicle.textContent = '車両を選択';
        noVehicle.addEventListener('click', function () {
          window.dispatchEvent(new CustomEvent(VEHICLE_CHANGE_REQUEST_EVENT));
        });
        vehiclePill.appendChild(noVehicle);
      }
    }

    // Brand list
    var brandList = container.querySelector('[data-search-grid-brand-list]');
    if (brandList) {
      brandList.innerHTML = '';
      var selectedBrands = new Set(state.brand);
      facets.brands.forEach(function (b) {
        var li = document.createElement('li');
        var lab = document.createElement('label');
        var cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.value = b.vendor;
        cb.checked = selectedBrands.has(b.vendor);
        cb.addEventListener('change', function () {
          if (cb.checked) state.brand.push(b.vendor);
          else
            state.brand = state.brand.filter(function (x) {
              return x !== b.vendor;
            });
          state.page = 1;
          writeUrlState(state, true);
          refresh(container, { rerenderFacets: false });
        });
        lab.appendChild(cb);
        var span = document.createElement('span');
        span.textContent = b.vendor + ' (' + b.count + ')';
        lab.appendChild(span);
        li.appendChild(lab);
        brandList.appendChild(li);
      });
    }

    // Price range hint
    var rangeHint = container.querySelector('[data-search-grid-price-range]');
    if (rangeHint) {
      if (facets.priceRange.min != null && facets.priceRange.max != null) {
        rangeHint.textContent =
          'Range: ' +
          formatPrice(facets.priceRange.min, 'USD') +
          ' – ' +
          formatPrice(facets.priceRange.max, 'USD');
      } else {
        rangeHint.textContent = '';
      }
    }

    // Category / subcategory
    var catList = container.querySelector('[data-search-grid-category-list]');
    if (catList) {
      catList.innerHTML = '';
      facets.categories.forEach(function (c) {
        var li = document.createElement('li');
        var lab = document.createElement('label');
        var rb = document.createElement('input');
        rb.type = 'radio';
        rb.name = 'ark-search-grid-cat-' + (container.dataset.blockId || '');
        rb.value = c.handle;
        rb.checked = state.category === c.handle;
        rb.addEventListener('change', function () {
          state.category = rb.checked ? c.handle : null;
          state.subcategory = null;
          state.page = 1;
          writeUrlState(state, true);
          refresh(container, { rerenderFacets: true });
        });
        lab.appendChild(rb);
        var span = document.createElement('span');
        span.textContent = (c.name || c.handle) + ' (' + c.count + ')';
        lab.appendChild(span);
        li.appendChild(lab);
        catList.appendChild(li);
      });
      // "Any" option
      var anyLi = document.createElement('li');
      var anyLab = document.createElement('label');
      var anyRb = document.createElement('input');
      anyRb.type = 'radio';
      anyRb.name = 'ark-search-grid-cat-' + (container.dataset.blockId || '');
      anyRb.value = '';
      anyRb.checked = !state.category;
      anyRb.addEventListener('change', function () {
        if (anyRb.checked) {
          state.category = null;
          state.subcategory = null;
          state.page = 1;
          writeUrlState(state, true);
          refresh(container, { rerenderFacets: true });
        }
      });
      anyLab.appendChild(anyRb);
      var anySpan = document.createElement('span');
      anySpan.textContent = '指定なし';
      anyLab.appendChild(anySpan);
      anyLi.appendChild(anyLab);
      catList.insertBefore(anyLi, catList.firstChild);
    }

    // Subcategory: show only if category selected and facets has subcategories
    var subSection = container.querySelector('[data-filter-section="subcategory"]');
    var subList = container.querySelector('[data-search-grid-subcategory-list]');
    if (subSection && subList) {
      if (state.category && facets.subcategories.length > 0) {
        subSection.hidden = false;
        subList.innerHTML = '';
        facets.subcategories.forEach(function (s) {
          var li = document.createElement('li');
          var lab = document.createElement('label');
          var rb = document.createElement('input');
          rb.type = 'radio';
          rb.name = 'ark-search-grid-subcat-' + (container.dataset.blockId || '');
          rb.value = s.handle;
          rb.checked = state.subcategory === s.handle;
          rb.addEventListener('change', function () {
            state.subcategory = rb.checked ? s.handle : null;
            state.page = 1;
            writeUrlState(state, true);
            refresh(container, { rerenderFacets: false });
          });
          lab.appendChild(rb);
          var span = document.createElement('span');
          span.textContent = (s.name || s.handle) + ' (' + s.count + ')';
          lab.appendChild(span);
          li.appendChild(lab);
          subList.appendChild(li);
        });
      } else {
        subSection.hidden = true;
      }
    }

    // Filter count badge (mobile)
    var filterCount = container.querySelector('[data-search-grid-filter-count]');
    if (filterCount) {
      // available と includeUniversal は default 値からの逸脱だけカウント
      // (default: available=true / includeUniversal=false)
      var n =
        state.brand.length +
        (state.priceMin != null ? 1 : 0) +
        (state.priceMax != null ? 1 : 0) +
        (state.available === false ? 1 : 0) +
        (state.includeUniversal === true ? 1 : 0) +
        (state.category ? 1 : 0) +
        (state.subcategory ? 1 : 0);
      if (n > 0) {
        filterCount.hidden = false;
        filterCount.textContent = String(n);
      } else {
        filterCount.hidden = true;
      }
    }
  }

  // ---------------------------------------------------------------------
  // Main render flow
  // ---------------------------------------------------------------------

  function showLoading(container) {
    var summary = container.querySelector('[data-search-grid-summary]');
    if (summary) summary.textContent = '読み込み中...';
    // skeleton 既に SSR されてるので何もしなくていい (初回)
    // 2 回目以降は grid を空にしてから skeleton 復元しない (UX 上速いだけでも見せる)
  }

  function showError(container) {
    var errEl = container.querySelector('[data-search-grid-error]');
    if (errEl) errEl.hidden = false;
    var emptyEl = container.querySelector('[data-search-grid-empty]');
    if (emptyEl) emptyEl.hidden = true;
  }

  function showEmpty(container, vehicle) {
    var emptyEl = container.querySelector('[data-search-grid-empty]');
    if (emptyEl) emptyEl.hidden = false;
    var emptyText = container.querySelector('[data-search-grid-empty-text]');
    if (emptyText) {
      emptyText.textContent = vehicle
        ? vehicleLabel(vehicle) + ' に適合するパーツが、現在の条件では見つかりませんでした。'
        : '現在の条件に適合するパーツが見つかりませんでした。';
    }
  }

  function hideEmpty(container) {
    var emptyEl = container.querySelector('[data-search-grid-empty]');
    if (emptyEl) emptyEl.hidden = true;
    var errEl = container.querySelector('[data-search-grid-error]');
    if (errEl) errEl.hidden = true;
  }

  function renderGrid(container, items, total, page, limit) {
    var grid = container.querySelector('[data-search-grid-cards]');
    if (!grid) return;
    grid.innerHTML = '';
    if (items.length === 0) {
      return; // empty state は別 element
    }
    var frag = document.createDocumentFragment();
    items.forEach(function (it) {
      frag.appendChild(renderCard(it));
    });
    grid.appendChild(frag);
    renderPagination(container, total, page, limit);
  }

  function refresh(container, opts) {
    opts = opts || {};
    var vehicle = loadVehicle();
    var state = container.__arkState;
    DBG('refresh', { state: state, vehicle: vehicle });
    showLoading(container);
    hideEmpty(container);

    // search + facets を並列 fetch。
    // opts.search は「乗っ取るか native を残すか」を決めるために collection ページの
    // 初回で既に投げた結果。同じ条件なので投げ直さない。
    Promise.all([
      opts.search ? Promise.resolve(opts.search) : fetchSearch(vehicle, state, container),
      opts.rerenderFacets === false
        ? Promise.resolve(null)
        : fetchFacets(vehicle, state, container).catch(function () {
            return null;
          }),
    ])
      .then(function (results) {
        var search = results[0];
        var facets = results[1];

        // Summary text
        var summary = container.querySelector('[data-search-grid-summary]');
        if (summary) {
          if (search.total === 0) {
            summary.textContent = '0 件';
          } else {
            summary.textContent =
              search.total + ' 件' + (vehicle ? ' — ' + vehicleLabel(vehicle) + ' 適合' : '');
          }
        }

        if (search.items.length === 0) {
          renderGrid(container, [], 0, search.page, search.limit);
          showEmpty(container, vehicle);
        } else {
          renderGrid(container, search.items, search.total, search.page, search.limit);
        }

        if (facets) {
          renderSidebar(container, facets, state);
        }
      })
      .catch(function (err) {
        DBG('refresh error', err);
        showError(container);
        var summary = container.querySelector('[data-search-grid-summary]');
        if (summary) summary.textContent = '読み込みエラー';
      });
  }

  var debouncedRefresh = (function () {
    var timer = null;
    return function (container, opts) {
      clearTimeout(timer);
      timer = setTimeout(function () {
        refresh(container, opts);
      }, DEBOUNCE_MS);
    };
  })();

  // ---------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------

  // Native grid の畳み込み。
  //
  // 以前は display:none で消していたが、native grid は **SSR 層**でもある:
  // custom grid は API 経由の JS 描画なので、JS を実行しないクローラ (GPTBot /
  // ClaudeBot 等) がコレクションの中身とページネーションを読めるのは native grid だけ。
  // display:none だと JS を実行する Googlebot 側で評価が落ちうるため、DOM に残したまま
  // <details> で畳んで「たたまれた領域」にする (タブ/アコーディオンと同じ扱いになる)。
  //
  // ⚠ **元のセクションの外へ動かさないこと。** Empire の static-collection は
  // `#shopify-section-<id>` 配下を起点に grid / filters / sticky controls を探す。
  // このスクリプトは defer で、非同期ロードの empire.js より先に走りうる。
  // 先に動かしてしまうと constructor が要素を見つけられず、モバイルでは sticky
  // 要素の参照で落ちてテーマ初期化ごと止まる。**その場で包むだけ**にする。
  var FOLD_CLASS = 'ark-native-fold';

  function foldNativeGrid(container) {
    var nativeSel = container.dataset.nativeGridSelector;
    if (!nativeSel) return;
    document.querySelectorAll(nativeSel).forEach(function (el) {
      if (el.__arkFoldWrap) return; // 二重適用防止 (再 activate 時)
      var details = document.createElement('details');
      details.className = FOLD_CLASS;
      // <details> には summary が要る (無いとブラウザが既定ラベルを出す)。
      // ただし CSS で非表示にしている — custom grid が同じ商品をより良い絞り込みで
      // 見せているので、人間が開く動機が無いため。中身は DOM に残るので
      // クローラ視点は変わらない。
      var summary = document.createElement('summary');
      summary.className = FOLD_CLASS + '__summary';
      summary.textContent = '絞り込みなしの全商品一覧を表示';
      details.appendChild(summary);
      // 復元用に元の位置を覚えておく (deactivate で戻す)
      el.__arkFoldHome = { parent: el.parentNode, next: el.nextSibling };
      el.parentNode.insertBefore(details, el);
      details.appendChild(el);
      el.__arkFoldWrap = details;
    });
  }

  function unfoldNativeGrid(container) {
    var nativeSel = container.dataset.nativeGridSelector;
    if (!nativeSel) return;
    document.querySelectorAll('.' + FOLD_CLASS + ' ' + nativeSel).forEach(function (el) {
      var wrap = el.__arkFoldWrap;
      var home = el.__arkFoldHome;
      if (!wrap || !home || !home.parent) return;
      home.parent.insertBefore(el, home.next);
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      el.__arkFoldWrap = null;
      el.__arkFoldHome = null;
    });
  }

  function activate(container) {
    foldNativeGrid(container);
    container.hidden = false;
  }

  function deactivate(container) {
    container.hidden = true;
    unfoldNativeGrid(container);
  }

  function bindControls(container, state) {
    // Sort
    var sortSel = container.querySelector('[data-search-grid-sort]');
    if (sortSel) {
      sortSel.value = state.sort;
      sortSel.addEventListener('change', function () {
        state.sort = sortSel.value;
        state.page = 1;
        writeUrlState(state, true);
        refresh(container, { rerenderFacets: false });
      });
    }

    // Price
    var priceMin = container.querySelector('[data-search-grid-price-min]');
    var priceMax = container.querySelector('[data-search-grid-price-max]');
    if (priceMin) {
      priceMin.value = state.priceMin != null ? String(state.priceMin) : '';
      priceMin.addEventListener('change', function () {
        state.priceMin = priceMin.value ? parseFloat(priceMin.value) : null;
        state.page = 1;
        writeUrlState(state, true);
        debouncedRefresh(container, { rerenderFacets: true });
      });
    }
    if (priceMax) {
      priceMax.value = state.priceMax != null ? String(state.priceMax) : '';
      priceMax.addEventListener('change', function () {
        state.priceMax = priceMax.value ? parseFloat(priceMax.value) : null;
        state.page = 1;
        writeUrlState(state, true);
        debouncedRefresh(container, { rerenderFacets: true });
      });
    }

    // Availability toggle (default: true = in stock only)
    var availCb = container.querySelector('[data-search-grid-available]');
    if (availCb) {
      availCb.checked = state.available === true;
      availCb.addEventListener('change', function () {
        state.available = availCb.checked;
        state.page = 1;
        writeUrlState(state, true);
        debouncedRefresh(container, { rerenderFacets: true });
      });
    }

    // Include universal toggle (default: false = vehicle 適合のみ)
    var univCb = container.querySelector('[data-search-grid-include-universal]');
    if (univCb) {
      univCb.checked = state.includeUniversal === true;
      univCb.addEventListener('change', function () {
        state.includeUniversal = univCb.checked;
        state.page = 1;
        writeUrlState(state, true);
        debouncedRefresh(container, { rerenderFacets: true });
      });
    }

    // Brand search (client-side filter for sidebar checkboxes)
    var brandSearch = container.querySelector('[data-search-grid-brand-search]');
    if (brandSearch) {
      brandSearch.addEventListener('input', function () {
        var q = brandSearch.value.toLowerCase().trim();
        var lis = container.querySelectorAll('[data-search-grid-brand-list] li');
        lis.forEach(function (li) {
          var label = (li.textContent || '').toLowerCase();
          li.hidden = q ? label.indexOf(q) === -1 : false;
        });
      });
    }

    // Clear all
    var clearAll = container.querySelector('[data-search-grid-clear-all]');
    if (clearAll) {
      clearAll.addEventListener('click', function () {
        // collection の scope は state ではなく container 側 (__scopeCollection) に
        // 持たせているので、ここで消しても外れない
        state.brand = [];
        state.priceMin = null;
        state.priceMax = null;
        // default に戻す: in stock only ON、universal は除外
        state.available = true;
        state.includeUniversal = false;
        state.category = null;
        state.subcategory = null;
        state.sort = isNewestCollection(container) ? 'newest' : 'relevance';
        state.page = 1;
        if (priceMin) priceMin.value = '';
        if (priceMax) priceMax.value = '';
        if (availCb) availCb.checked = true;
        if (univCb) univCb.checked = false;
        if (sortSel) sortSel.value = state.sort;
        writeUrlState(state, true);
        refresh(container, { rerenderFacets: true });
      });
    }

    // Empty state buttons
    var emptyClear = container.querySelector('[data-search-grid-empty-clear]');
    if (emptyClear) {
      emptyClear.addEventListener('click', function () {
        if (clearAll) clearAll.click();
      });
    }
    var emptyChangeVehicle = container.querySelector('[data-search-grid-empty-change-vehicle]');
    if (emptyChangeVehicle) {
      emptyChangeVehicle.addEventListener('click', function () {
        window.dispatchEvent(new CustomEvent(VEHICLE_CHANGE_REQUEST_EVENT));
      });
    }

    // Retry on error
    var retry = container.querySelector('[data-search-grid-retry]');
    if (retry) {
      retry.addEventListener('click', function () {
        refresh(container, { rerenderFacets: true });
      });
    }

    // Mobile sidebar toggle
    var mobileToggle = container.querySelector('[data-search-grid-mobile-toggle]');
    var sidebar = container.querySelector('[data-search-grid-sidebar]');
    var mobileClose = container.querySelector('[data-search-grid-sidebar-close]');
    if (mobileToggle && sidebar) {
      mobileToggle.addEventListener('click', function () {
        sidebar.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });
    }
    if (mobileClose && sidebar) {
      mobileClose.addEventListener('click', function () {
        sidebar.classList.remove('is-open');
        document.body.style.overflow = '';
      });
    }
  }

  // ---------------------------------------------------------------------
  // Collection-scope helpers (grid を通常 collection テンプレに配置した場合)
  // ---------------------------------------------------------------------
  function normalizeKey(s) {
    return (s || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  // 新着系コレクションだけ既定の並びを newest にする (絞り込みではなく並びの初期値)。
  // handle と title の両方を見る。日本語 title は normalizeKey で空に潰れるので handle が主。
  function isNewestCollection(container) {
    var keys = [
      normalizeKey(container.dataset.collectionHandle),
      normalizeKey(container.dataset.collectionTitle),
    ]
      .filter(Boolean)
      .join(' ');
    return /newarrival|newproduct|newest|justarrived|latest/.test(keys);
  }

  function attachStateListeners(container, showWhen) {
    // vehicle change → 再 fetch
    window.addEventListener(VEHICLE_CHANGED_EVENT, function () {
      var v = loadVehicle();
      if (v) {
        activate(container);
        container.__arkState.page = 1;
        writeUrlState(container.__arkState, true);
        refresh(container, { rerenderFacets: true });
      } else if (showWhen === 'vehicle_set') {
        deactivate(container);
      }
    });

    // popstate (back/forward)。collection の scope は URL でなく container 側に
    // 持っているので、履歴を戻っても外れない
    window.addEventListener('popstate', function () {
      var s = readUrlState();
      if (isNewestCollection(container) && (!s.sort || s.sort === 'relevance')) s.sort = 'newest';
      container.__arkState = s;
      refresh(container, { rerenderFacets: true });
    });
  }

  // preloadedSearch: 呼び出し側が既に同条件で取得済みの検索結果 (あれば再取得しない)。
  function startGrid(container, preloadedSearch) {
    activate(container);
    bindControls(container, container.__arkState);
    refresh(container, { rerenderFacets: true, search: preloadedSearch });
    attachStateListeners(container, container.dataset.showWhen || 'vehicle_set');
  }

  // Collection テンプレ: fitment category にマップすると確認できて初めて native
  // grid を乗っ取る。brand/sale collection の誤爆と native→custom→native の
  // ちらつきを防ぐ。
  function initCollectionScoped(container, vehicle) {
    var state = readUrlState();
    container.__arkState = state;

    // コレクションのスコープは handle だけで決まる。PIM 側が ProductCollection の所属で
    // 絞るので、facet との名前照合は要らない。
    //
    // 以前は handle/title を facet リストと照合してマップできた時だけ乗っ取っていたが、
    // facet は brands 100 / categories 50 / subcategories 100 で打ち切られており、
    // 公開 1,124 コレクションのうち 159 件しか解決できていなかった (live 実測 2026-08-06)。
    //
    // 絞り込みの条件であってユーザーが外せる filter ではないので state には入れない。
    container.__scopeCollection = container.dataset.collectionHandle;

    if (isNewestCollection(container) && (!state.sort || state.sort === 'relevance')) {
      state.sort = 'newest';
    }

    // 1 件も返らないコレクションでは native grid を残す。
    // 所属同期がまだ走っていない / PIM が知らない商品だけのコレクションで、
    // 「native なら商品が出るのに空の grid を見せる」のを防ぐ fail-safe。
    fetchSearch(vehicle, state, container)
      .then(function (data) {
        if (!data || !data.total) {
          DBG('collection has no PIM-known products → leaving native grid intact');
          return; // hidden のまま native grid を表示
        }
        startGrid(container, data);
      })
      .catch(function (err) {
        DBG('collection scope resolution failed → leaving native grid', err);
        // fail-safe: native grid を維持。スコープを残すと以後の fetch も失敗するので剥がす。
        container.__scopeCollection = null;
      });
  }

  function initContainer(container) {
    var showWhen = container.dataset.showWhen || 'vehicle_set';
    var vehicle = loadVehicle();

    if (showWhen === 'vehicle_set' && !vehicle) {
      DBG('no vehicle set, leaving native grid intact');
      return; // hidden のまま
    }

    // Collection-scoped mount (collection テンプレに scope 有効で配置)
    if (container.dataset.collectionHandle) {
      initCollectionScoped(container, vehicle);
      return;
    }

    // Standalone mount (find-parts / page テンプレ) — 従来動作
    container.__arkState = readUrlState();
    startGrid(container);
  }

  function init() {
    document.querySelectorAll('[data-fitment-search-grid]').forEach(initContainer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
